import { all, call, fork, select, put, takeLatest } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';

import { simpleRequestsLoader } from 'modules/app/utils';
import { deleteGateSuccess } from 'modules/gates/actions/core';
import * as types from 'modules/gates/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';
import { downloadURL } from 'services/Utils';

import gateDashboardSaga from './dashboard';
import gateDetailsSaga from './details';
import { downloadPublicQRCodeFailure, downloadPublicQRCodeSuccess, fetchGateOpeningsSuccess } from '../actions/details';
import { getGateById } from '../selectors/core';

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.CREATE_GATE]: {
    notifications: {
      success: {
        id: 'page.gate.create.success',
        defaultMessage: 'Gate created successfully.',
      },
      failure: {
        id: 'page.gate.create.error',
        defaultMessage: 'An error occurred while creating the gate.',
      },
    },
  },
  [types.DELETE_GATE]: {
    notifications: {
      success: {
        id: 'page.gate.delete.success',
        defaultMessage: 'Gate deleted successfully.',
      },
      failure: {
        id: 'page.gate.delete.error',
        defaultMessage: 'An error occurred while deleting the gate.',
      },
    },
    callbacks: {
      success: (_, meta, { payload: { gateId } }) => deleteGateSuccess(gateId, meta),
    },
  },
  [types.UPDATE_GATE]: {
    notifications: {
      success: {
        id: 'page.gate.update.success',
        defaultMessage: 'Gate updated successfully.',
      },
      failure: {
        id: 'page.gate.update.error',
        defaultMessage: 'An error occurred while updating the gate.',
      },
    },
  },
  [types.RESTRICTED_ACCESS_RELAY_CHECK]: {
    notifications: {
      success: {
        id: 'page.kiosks.restrictedAccess.relayCheck.success',
        defaultMessage: 'Connection to gate successful.',
      },
      failure: {
        id: 'page.kiosks.restrictedAccess.relayCheck.failure',
        defaultMessage: 'The connection to the gate failed.',
      },
    },
  },
  [types.FETCH_GATE_OPENINGS]: {
    callbacks: {
      success: (data, meta, action) => {
        const { gateId } = action.payload;
        const isAppend = Boolean(action.payload.request.params?.lastSk);
        return fetchGateOpeningsSuccess(data, gateId, meta, isAppend);
      },
    },
  },
};

function* downloadPublicQRCode({ payload, meta }) {
  const { request, gateId } = payload;
  try {
    const { data } = yield call(restClient.request, { ...request, responseType: 'arraybuffer' });
    yield put(downloadPublicQRCodeSuccess(data, meta));

    const gate = yield select(getGateById, gateId);
    const fileName = gate?.name ? `${gate.name}.png` : 'gate-qr-code.png';

    const blob = new Blob([data], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    downloadURL(url, fileName);
    URL.revokeObjectURL(url);
  } catch (error) {
    yield put(downloadPublicQRCodeFailure(error, meta));
    const notification = {
      type: 'error',
      message: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    };
    yield put(createNotification(notification));
  }
}

export default function* gateCoreSaga() {
  yield takeLatest(types.DOWNLOAD_PUBLIC_QR_CODE_REQUEST, downloadPublicQRCode);
  yield all(simpleRequestsLoader(simpleRequests));
  yield fork(gateDashboardSaga);
  yield fork(gateDetailsSaga);
}
